import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "dropzone",
    "input",
    "preview",
    "previewContent",
    "cameraButton",
    "cameraModal",
    "cameraVideo",
    "cameraCanvas",
    "captureButton",
    "finalForm",
    "finalInput",
    "finalInputBack",
    "perspectiveCropButton",
    "processingOverlay",
    "secondCardPrompt",
    "sideIndicator",
    "scanModeInput",
    "cameraSideLabel",
    "cameraInstructions",
    "step1",
    "step2",
    "instructionText",
    "captureButtonText",
    "skipButton",
    "successMessage",
    "switchCameraButton",
  ];

  connect() {
    console.log("Upload controller connected");
    this.currentFile = null;
    this.currentFileBack = null;
    this.originalFileName = null;
    this.originalFileNameBack = null;
    this.hasCroppedImage = false;
    this.hasCroppedImageBack = false;
    this.suggestedCorners = null;
    this.suggestedCornersBack = null;
    this.uploadIntroSection = document.querySelector('.upload-intro');
    this.uploadOptionsSection = document.querySelector('.upload-options');
    this.isFileDialogOpen = false;
    this.scanMode = this.getScanMode();
    this.currentSide = 'front'; // Track which side we're currently working with
    this.availableCameras = []; // Store available camera devices
    this.currentCameraIndex = 0; // Track which camera is currently selected
    this.currentStream = null; // Store current stream reference
  }

  disconnect() {
    // Release the camera when navigating away (Turbo doesn't reload the page)
    if (this.hasCameraVideoTarget) {
      this.stopCameraStream();
    }
    document.body.style.overflow = "";
  }

  getScanMode() {
    if (this.hasScanModeInputTarget) {
      return this.scanModeInputTarget.value;
    }
    return 'back_only';
  }

  async updateScanMode(event) {
    const newMode = event.target.value;
    this.scanMode = newMode;
    
    // Update the hidden input
    if (this.hasScanModeInputTarget) {
      this.scanModeInputTarget.value = newMode;
    }
    
    // Save preference to server
    try {
      const token = document.querySelector('meta[name="csrf-token"]').content;
      const response = await fetch('/home/contacts/toggle_scan_mode', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token,
          'Accept': 'application/json',
        },
        body: JSON.stringify({ mode: newMode }),
      });
      
      if (response.ok) {
        // Visual feedback - briefly highlight the selected option
        const selectedOption = event.target.closest('.scan-mode-toggle__option');
        if (selectedOption) {
          selectedOption.style.animation = 'pulse 0.5s ease-in-out';
          setTimeout(() => {
            selectedOption.style.animation = '';
          }, 500);
        }
        console.log(`Scan mode updated to: ${newMode}`);
      } else {
        console.error('Failed to save scan mode preference');
      }
    } catch (error) {
      console.error('Error saving scan mode preference:', error);
    }
  }

  startCameraForSecondSide() {
    this.currentSide = 'back';
    this.startCamera();
  }

  skipCurrentSide() {
    if (this.scanMode === 'both_sides' && this.currentSide === 'front') {
      // Skip front, move to back
      this.currentSide = 'back';
      this.updateCameraInstructions();
    } else if (this.scanMode === 'both_sides' && this.currentSide === 'back') {
      // Skip back, finish with just front
      if (this.currentFile) {
        this.closeCamera();
        this.proceedWithOneSide();
      } else {
        // No files at all, just close
        this.closeCamera();
      }
    }
  }

  updateCameraInstructions() {
    if (!this.hasCameraSideLabelTarget) return;

    if (this.scanMode === 'both_sides') {
      if (this.currentSide === 'front') {
        this.cameraSideLabelTarget.textContent = 'Front Side';
        if (this.hasInstructionTextTarget) {
          this.instructionTextTarget.innerHTML = 'Position the <strong>front side</strong> of the business card within the frame. Make sure all text is clearly visible.';
        }
        if (this.hasCaptureButtonTarget) {
          const textSpan = this.captureButtonTarget.querySelector('[data-upload-target="captureButtonText"]');
          if (textSpan) {
            textSpan.textContent = 'Capture';
          }
        }
        if (this.hasStep1Target && this.hasStep2Target) {
          this.step1Target.setAttribute('data-step', 'active');
          this.step2Target.setAttribute('data-step', 'pending');
        }
        if (this.hasSkipButtonTarget) {
          this.skipButtonTarget.style.display = 'inline-block';
        }
      } else {
        this.cameraSideLabelTarget.textContent = 'Back Side';
        if (this.hasInstructionTextTarget) {
          this.instructionTextTarget.innerHTML = 'Now position the <strong>back side</strong> of the business card. This usually contains the main contact information.';
        }
        if (this.hasCaptureButtonTarget) {
          const textSpan = this.captureButtonTarget.querySelector('[data-upload-target="captureButtonText"]');
          if (textSpan) {
            textSpan.textContent = 'Capture';
          }
        }
        if (this.hasStep1Target && this.hasStep2Target) {
          this.step1Target.setAttribute('data-step', 'completed');
          this.step2Target.setAttribute('data-step', 'active');
        }
        if (this.hasSkipButtonTarget) {
          this.skipButtonTarget.style.display = this.currentFile ? 'inline-block' : 'none';
        }
      }
    } else {
      // Back only mode
      this.cameraSideLabelTarget.textContent = 'Back Side';
      if (this.hasInstructionTextTarget) {
        this.instructionTextTarget.innerHTML = 'Position the <strong>back side</strong> of the business card within the frame. The back usually contains the main contact information.';
      }
      if (this.hasCaptureButtonTarget) {
        const textSpan = this.captureButtonTarget.querySelector('[data-upload-target="captureButtonText"]');
        if (textSpan) {
          textSpan.textContent = 'Capture Business Card';
        }
      }
      if (this.hasCameraInstructionsTarget) {
        // Hide step indicator for single scan mode
        const stepIndicator = this.cameraInstructionsTarget.querySelector('.camera-modal__step-indicator');
        if (stepIndicator) {
          stepIndicator.style.display = 'none';
        }
      }
      if (this.hasSkipButtonTarget) {
        this.skipButtonTarget.style.display = 'none';
      }
    }
  }

  clickInput(event) {
    console.log("Click input triggered");
    
    // Prevent event propagation to avoid double triggers
    if (event) {
      event.stopPropagation();
    }
    
    // Guard against rapid clicks while file dialog is open
    if (this.isFileDialogOpen) {
      console.log("File dialog already open, skipping");
      return;
    }
    
    this.isFileDialogOpen = true;
    this.inputTarget.click();
    
    // Reset flag after a delay to handle dialog close
    setTimeout(() => {
      this.isFileDialogOpen = false;
    }, 500);
  }

  async enumerateCameras() {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      this.availableCameras = devices.filter(device => device.kind === 'videoinput');
      console.log(`Found ${this.availableCameras.length} camera(s):`, this.availableCameras);
      
      // If we have multiple cameras, prefer environment-facing cameras
      const envCameras = this.availableCameras.filter(cam => 
        cam.label.toLowerCase().includes('back') || 
        cam.label.toLowerCase().includes('rear') ||
        cam.label.toLowerCase().includes('environment')
      );
      
      if (envCameras.length > 0) {
        // Find the main (non-wide, non-ultra-wide) camera if possible
        const mainCamera = envCameras.find(cam => 
          !cam.label.toLowerCase().includes('wide') &&
          !cam.label.toLowerCase().includes('ultra')
        );
        
        if (mainCamera) {
          this.currentCameraIndex = this.availableCameras.indexOf(mainCamera);
          console.log('Selected main rear camera:', mainCamera.label);
        } else {
          this.currentCameraIndex = this.availableCameras.indexOf(envCameras[0]);
          console.log('Selected first rear camera:', envCameras[0].label);
        }
      }
      
      return this.availableCameras;
    } catch (error) {
      console.error('Error enumerating cameras:', error);
      return [];
    }
  }

  async switchCamera() {
    if (this.availableCameras.length <= 1) {
      console.log('No other cameras available to switch to');
      return;
    }
    
    // Cycle to next camera
    this.currentCameraIndex = (this.currentCameraIndex + 1) % this.availableCameras.length;
    console.log('Switching to camera:', this.availableCameras[this.currentCameraIndex].label);
    
    // Stop current stream and restart with new camera
    this.stopCameraStream();
    await this.startCameraWithDevice(this.availableCameras[this.currentCameraIndex].deviceId);
  }

  stopCameraStream() {
    if (this.currentStream) {
      const tracks = this.currentStream.getTracks();
      tracks.forEach((track) => track.stop());
      this.currentStream = null;
    }
    if (this.cameraVideoTarget.srcObject) {
      this.cameraVideoTarget.srcObject = null;
    }
  }

  // Attach a stream to the video element and start playback explicitly:
  // re-attaching a stream to the same element does not reliably re-trigger
  // autoplay, which leaves the camera preview black until a full page reload.
  async attachStream(stream) {
    this.currentStream = stream;
    this.cameraVideoTarget.srcObject = stream;
    try {
      await this.cameraVideoTarget.play();
    } catch (error) {
      console.warn("Camera video play() was blocked:", error);
    }
  }

  async startCameraWithDevice(deviceId) {
    try {
      const constraints = {
        video: {
          deviceId: deviceId ? { exact: deviceId } : undefined,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        }
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      await this.attachStream(stream);
    } catch (error) {
      console.error('Error starting camera with device:', error);
      throw error;
    }
  }

  async startCamera() {
    // Reset to appropriate side based on scan mode
    if (this.scanMode === 'both_sides' && !this.currentFile) {
      this.currentSide = 'front';
    } else if (this.scanMode === 'back_only') {
      this.currentSide = 'back';
    }
    
    // Update camera instructions before showing
    this.updateCameraInstructions();
    
    try {
      // Check if camera is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error("Camera not supported by this browser");
      }

      // Enumerate cameras to find the best one
      await this.enumerateCameras();

      let stream;
      
      // Try with selected camera device if available
      if (this.availableCameras.length > 0 && this.availableCameras[this.currentCameraIndex]) {
        try {
          await this.startCameraWithDevice(this.availableCameras[this.currentCameraIndex].deviceId);
          stream = this.currentStream;
        } catch (deviceError) {
          console.log("Selected device failed, trying fallback:", deviceError);
          throw deviceError;
        }
      } else {
        // Fallback to facingMode approach
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: {
              facingMode: "environment",
              width: { ideal: 1920 },
              height: { ideal: 1080 },
            },
          });
          await this.attachStream(stream);
        } catch (envError) {
          console.log("Environment camera failed, trying user camera:", envError);

          try {
            stream = await navigator.mediaDevices.getUserMedia({
              video: {
                facingMode: "user",
                width: { ideal: 1280 },
                height: { ideal: 720 },
              },
            });
            await this.attachStream(stream);
          } catch (userError) {
            console.log("User camera failed, trying basic video:", userError);

            stream = await navigator.mediaDevices.getUserMedia({
              video: true,
            });
            await this.attachStream(stream);
          }
        }
      }

      this.cameraModalTarget.style.display = "block";
      document.body.style.overflow = "hidden";
      
      // Show/hide switch camera button based on available cameras
      if (this.hasSwitchCameraButtonTarget) {
        if (this.availableCameras.length > 1) {
          this.switchCameraButtonTarget.style.display = 'inline-flex';
          console.log(`Using camera ${this.currentCameraIndex + 1} of ${this.availableCameras.length}: ${this.availableCameras[this.currentCameraIndex].label}`);
        } else {
          this.switchCameraButtonTarget.style.display = 'none';
        }
      }
    } catch (error) {
      console.error("Error accessing camera:", error);
      
      let message = "Unable to access camera. ";
      if (error.name === "NotAllowedError") {
        message += "Please grant camera permissions and try again.";
      } else if (error.name === "NotFoundError") {
        message += "No camera found on this device.";
      } else if (error.name === "NotSupportedError") {
        message += "Camera is not supported by this browser.";
      } else if (error.name === "NotReadableError") {
        message += "Camera is already in use by another application. Please close other apps using the camera and try again.";
      } else if (location.protocol !== "https:" && location.hostname !== "localhost") {
        message += "Camera requires HTTPS or localhost. Please use a secure connection.";
      } else {
        message += "Please check your camera settings and try again.";
      }
      
      alert(message);
    }
  }

  closeCamera() {
    this.stopCameraStream();
    this.cameraModalTarget.style.display = "none";
    document.body.style.overflow = "";
  }

  capturePhoto() {
    const video = this.cameraVideoTarget;
    const canvas = this.cameraCanvasTarget;
    const context = canvas.getContext("2d");

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
        const file = new File([blob], `business-card-${timestamp}.jpg`, {
          type: "image/jpeg",
        });

        // Handle based on scan mode
        console.log(`Captured photo - Mode: ${this.scanMode}, Side: ${this.currentSide}`);
        
        if (this.scanMode === 'both_sides') {
          if (this.currentSide === 'front') {
            console.log('Processing front side, keeping camera open for back');
            // First side captured - DON'T close camera, process and continue
            this.uploadForServerProcessing(file, 'front');
          } else if (this.currentSide === 'back') {
            console.log('Processing back side, closing camera after');
            // Second side captured - NOW close camera after processing
            this.closeCamera();
            this.uploadForServerProcessing(file, 'back');
          }
        } else {
          console.log('Back only mode - closing camera');
          // Back only mode - close camera after capture
          this.closeCamera();
          this.uploadForServerProcessing(file, 'back');
        }
      },
      "image/jpeg",
      0.9,
    );
  }

  async uploadForServerProcessing(file, side = null) {
    // Determine which side we're processing
    const actualSide = side || (this.scanMode === 'both_sides' ? this.currentSide : 'back');

    // Drop any corner suggestion left over from a previous failed attempt
    if (actualSide === 'front') {
      this.suggestedCorners = null;
    } else {
      this.suggestedCornersBack = null;
    }

    try {
      console.log(`Starting upload processing - Side: ${actualSide}, Scan Mode: ${this.scanMode}`);
      
      // Only show processing overlay if we're not keeping camera open
      const keepCameraOpen = this.scanMode === 'both_sides' && actualSide === 'front';
      if (keepCameraOpen) {
        // Show inline processing indicator for front side
        this.showInlineProcessing();
      } else {
        this.showProcessing(`Detecting and cropping ${actualSide === 'front' ? 'front' : 'back'} side`);
      }
      const formData = new FormData();
      formData.append("business_card", file);

      const token = document.querySelector('meta[name="csrf-token"]').content;

      const response = await fetch("/home/contacts/preview_processing", {
        method: "POST",
        headers: {
          "Accept": "image/jpeg",
          "X-CSRF-Token": token,
        },
        body: formData,
      });

      if (!response.ok) {
        const text = await response.text();
        console.error("Server-side detection failed:", text);
        this.fallbackToOriginalImage(file, actualSide, this.parseCornerSuggestion(text));
        return;
      }

      const processedBlob = await response.blob();
      const processedFile = new File([processedBlob], `processed-${file.name}`, { type: "image/jpeg" });

      // Store based on side
      if (actualSide === 'front') {
        this.currentFile = processedFile;
        this.originalFileName = processedFile.name;
        this.hasCroppedImage = true;
      } else {
        this.currentFileBack = processedFile;
        this.originalFileNameBack = processedFile.name;
        this.hasCroppedImageBack = true;
        // For back_only mode, also set hasCroppedImage since this is our main image
        if (this.scanMode === 'back_only') {
          this.hasCroppedImage = true;
        }
      }
      
      // Handle UI based on scan mode
      if (this.scanMode === 'both_sides' && actualSide === 'front') {
        console.log('Front side processed successfully, updating camera for back side');
        // Front processed, camera still open for back
        // Hide inline processing indicator
        this.hideInlineProcessing();
        // Show success message briefly
        if (this.hasSuccessMessageTarget) {
          this.successMessageTarget.style.display = 'flex';
          setTimeout(() => {
            this.successMessageTarget.style.display = 'none';
          }, 3000);
        }
        // Update UI for back side capture
        this.currentSide = 'back';
        this.updateCameraInstructions();
        // Don't show preview yet, camera modal should still be open
        return; // Important: exit early to keep camera open
      } else {
        console.log('Processing complete, showing preview');
        // Single card or second card completed
        // For both_sides mode with both cards, show both in preview
        if (this.scanMode === 'both_sides' && this.currentFile && this.currentFileBack) {
          this.showBothSidesPreview();
          this.perspectiveCropButtonTarget.textContent = "Process Business Card (Back Side)";
        } else {
          this.showPreview(actualSide === 'front' ? this.currentFile : this.currentFileBack, actualSide);
          this.perspectiveCropButtonTarget.textContent = "Process Business Card";
        }
        this.perspectiveCropButtonTarget.disabled = false;
        this.hideUploadOptions();
        this.hideProcessing();
      }
    } catch (e) {
      console.error("Error uploading for server processing:", e);
      this.fallbackToOriginalImage(file, actualSide);
    }
  }

  // The low-confidence 422 body carries classical OpenCV corner suggestions
  // for the manual crop UI; other failure bodies are plain text.
  parseCornerSuggestion(responseText) {
    try {
      const data = JSON.parse(responseText);
      if (Array.isArray(data.corners) && data.corners.length === 4) {
        return {
          corners: data.corners,
          imageWidth: data.image?.width,
          imageHeight: data.image?.height,
        };
      }
    } catch {
      // Plain-text error body — no suggestion available
    }
    return null;
  }

  // Server-side detection rejected the image or errored: keep the original
  // file in the slot the rest of the flow reads from, and let the user crop
  // it manually (showPreview auto-opens the perspective crop modal when
  // hasCroppedImage is false).
  fallbackToOriginalImage(file, actualSide, cornerSuggestion = null) {
    if (actualSide === 'front') {
      this.currentFile = file;
      this.originalFileName = file.name;
      this.hasCroppedImage = false;
      this.suggestedCorners = cornerSuggestion;
      if (this.scanMode === 'both_sides') {
        // Camera is still open — move on to capturing the back side
        this.hideInlineProcessing();
        this.currentSide = 'back';
        this.updateCameraInstructions();
        return;
      }
    } else {
      this.currentFileBack = file;
      this.originalFileNameBack = file.name;
      this.hasCroppedImageBack = false;
      this.suggestedCornersBack = cornerSuggestion;
      if (this.scanMode === 'back_only') {
        this.hasCroppedImage = false;
      }
    }
    this.showPreview(file, actualSide);
    this.hideProcessing();
  }

  showInlineProcessing() {
    // Add a processing indicator to the camera controls
    if (this.hasCaptureButtonTarget) {
      this.captureButtonTarget.disabled = true;
      this.captureButtonTarget.innerHTML = `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="animate-spin">
          <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
          <path d="M9 12l2 2 4-4" opacity="0.5"/>
        </svg>
        <span>Processing Front Side...</span>
      `;
    }
    if (this.hasSkipButtonTarget) {
      this.skipButtonTarget.disabled = true;
    }
  }

  hideInlineProcessing() {
    // Restore capture button
    if (this.hasCaptureButtonTarget) {
      this.captureButtonTarget.disabled = false;
      this.captureButtonTarget.innerHTML = `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"/>
          <circle cx="12" cy="12" r="6"/>
        </svg>
        <span>Capture Back Side</span>
      `;
      // Update the actual text content if we have the target
      if (this.hasCaptureButtonTextTarget) {
        this.captureButtonTextTarget.textContent = 'Capture Back Side';
      }
    }
    if (this.hasSkipButtonTarget) {
      this.skipButtonTarget.disabled = false;
    }
  }

  showSecondCardPrompt() {
    if (this.hasSecondCardPromptTarget) {
      this.secondCardPromptTarget.style.display = 'block';
      this.hideUploadOptions();
    }
  }

  hideSecondCardPrompt() {
    if (this.hasSecondCardPromptTarget) {
      this.secondCardPromptTarget.style.display = 'none';
    }
  }

  proceedWithOneSide() {
    // User chose to continue with only one side
    this.hideSecondCardPrompt();
    this.showPreview(this.currentFile, 'front');
    this.perspectiveCropButtonTarget.textContent = "Process Business Card";
    this.perspectiveCropButtonTarget.disabled = false;
  }

  secondFileSelected(event) {
    const file = event.target.files[0];
    if (file) {
      this.originalFileNameBack = file.name;
      if (file.type && file.type.startsWith("image/")) {
        this.uploadForServerProcessing(file, 'back');
      } else {
        this.currentFileBack = file;
        this.hasCroppedImageBack = false;
        this.showPreview(file, 'back');
      }
      this.hideSecondCardPrompt();
    }
  }

  showProcessing(message = "Processing…") {
    if (!this.hasProcessingOverlayTarget) return;
    const overlay = this.processingOverlayTarget;
    const textEl = overlay.querySelector(".processing__text");
    if (textEl) textEl.textContent = message;
    overlay.classList.add("is-visible");
  }

  hideProcessing() {
    if (!this.hasProcessingOverlayTarget) return;
    this.processingOverlayTarget.classList.remove("is-visible");
  }

  dragOver(event) {
    event.preventDefault();
    this.dropzoneTarget.classList.add("dragover");
  }

  dragLeave(event) {
    event.preventDefault();
    this.dropzoneTarget.classList.remove("dragover");
  }

  drop(event) {
    event.preventDefault();
    this.dropzoneTarget.classList.remove("dragover");

    const files = event.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      this.originalFileName = file.name;
      if (file.type && file.type.startsWith("image/")) {
        // Use server-side detection/cropping for images
        this.uploadForServerProcessing(file);
      } else {
        // Non-image fallback: keep existing preview flow
        this.currentFile = file;
        this.hasCroppedImage = false;
        this.showPreview(file);
      }
    }
  }

  fileSelected(event) {
    // Stop propagation to prevent triggering parent click handlers
    if (event) {
      event.stopPropagation();
    }
    
    const file = this.inputTarget.files[0];
    if (file) {
      console.log("File:", file.name, file.type, file.size);
      
      // Reset for new upload
      if (this.scanMode === 'both_sides') {
        this.currentFile = null;
        this.currentFileBack = null;
        this.currentSide = 'front';
      }
      
      this.originalFileName = file.name;
      if (file.type && file.type.startsWith("image/")) {
        // Use server-side detection/cropping for images
        this.uploadForServerProcessing(file, this.scanMode === 'both_sides' ? 'front' : 'back');
      } else {
        // Non-image fallback
        if (this.scanMode === 'both_sides') {
          this.currentFile = file;
          this.hasCroppedImage = false;
          this.showSecondCardPrompt();
        } else {
          this.currentFileBack = file;
          this.hasCroppedImageBack = false;
          this.showPreview(file, 'back');
        }
      }
    }
    
    // Reset the dialog flag
    this.isFileDialogOpen = false;
  }

  handleInputClick(event) {
    // Prevent the input's click event from bubbling up to the dropzone
    // This prevents the file dialog from opening twice
    console.log("Input click handler - stopping propagation");
    event.stopPropagation();
  }

  removeFile() {
    this.inputTarget.value = "";
    this.currentFile = null;
    this.currentFileBack = null;
    this.originalFileName = null;
    this.originalFileNameBack = null;
    this.currentSide = 'front';
    this.hidePreview();
    this.hideSecondCardPrompt();
  }

  showBothSidesPreview() {
    // Update side indicator
    if (this.hasSideIndicatorTarget) {
      this.sideIndicatorTarget.textContent = ' (Both Sides)';
    }
    
    // Create preview HTML for both cards
    let previewHTML = `
      <div class="file-preview__both-sides">
        <div class="file-preview__side-container">
          <h4 class="file-preview__side-label">Front Side</h4>
          <div class="file-preview__side-content" id="front-preview"></div>
        </div>
        <div class="file-preview__side-container">
          <h4 class="file-preview__side-label">Back Side</h4>
          <div class="file-preview__side-content" id="back-preview"></div>
        </div>
      </div>
      <div class="file-preview__info">
        <span class="file-preview__label">Scan Mode:</span>
        <span class="file-preview__value">Both Sides</span>
        <span class="file-preview__label">Processing:</span>
        <span class="file-preview__value">Back side will be used for AI extraction</span>
      </div>
    `;
    
    this.previewContentTarget.innerHTML = previewHTML;
    this.previewTarget.style.display = "block";
    
    // Load front side
    const frontReader = new FileReader();
    frontReader.onload = (e) => {
      const frontContainer = document.getElementById('front-preview');
      if (frontContainer) {
        frontContainer.innerHTML = `<img src="${e.target.result}" alt="Business card front" class="file-preview__image">`;
      }
    };
    frontReader.readAsDataURL(this.currentFile);
    
    // Load back side
    const backReader = new FileReader();
    backReader.onload = (e) => {
      const backContainer = document.getElementById('back-preview');
      if (backContainer) {
        backContainer.innerHTML = `<img src="${e.target.result}" alt="Business card back" class="file-preview__image">`;
      }
    };
    backReader.readAsDataURL(this.currentFileBack);
    
    this.perspectiveCropButtonTarget.disabled = false;
  }

  showPreview(file, side = 'back') {
    // Update side indicator
    if (this.hasSideIndicatorTarget) {
      if (this.scanMode === 'both_sides') {
        const sideText = this.currentFile && this.currentFileBack ? ' (Both Sides)' : ` (${side === 'front' ? 'Front' : 'Back'} Side)`;
        this.sideIndicatorTarget.textContent = sideText;
      } else {
        this.sideIndicatorTarget.textContent = '';
      }
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      let previewHTML = "";

      if (file.type.startsWith("image/")) {
        previewHTML = `<img src="${e.target.result}" alt="Business card preview" class="file-preview__image">`;
      } else if (file.type === "application/pdf") {
        previewHTML = `
          <div class="file-preview__pdf">
            <div class="file-preview__pdf-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2-2V8z"/>
                <polyline points="14,2 14,8 20,8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10,9 9,9 8,9"/>
              </svg>
            </div>
            <p>PDF Document Ready for Processing</p>
          </div>
        `;
      }

      previewHTML += `
        <div class="file-preview__info">
          <span class="file-preview__label">File Name:</span>
          <span class="file-preview__value">${file.name}</span>
          <span class="file-preview__label">File Size:</span>
          <span class="file-preview__value">${this.formatFileSize(file.size)}</span>
          <span class="file-preview__label">File Type:</span>
          <span class="file-preview__value">${file.type}</span>
        </div>
      `;

      this.previewContentTarget.innerHTML = previewHTML;
      this.previewTarget.style.display = "block";

      if (file.type.startsWith("image/")) {
        this.perspectiveCropButtonTarget.disabled = false;
        // Reset button text for new files
        if (!this.hasCroppedImage) {
          this.perspectiveCropButtonTarget.textContent = "Adjust Crop";
          // Automatically open perspective crop modal for new images after a brief delay
          setTimeout(() => {
            this.openPerspectiveCropModal();
          }, 800);
        }
      } else {
        this.perspectiveCropButtonTarget.disabled = true;
      }
    };
    reader.readAsDataURL(file);
  }



  perspectiveCropBusinessCard() {
    console.log("Perspective crop business card clicked");
    
    // Determine which file to use based on scan mode
    const fileToUse = this.scanMode === 'back_only' ? this.currentFileBack : this.currentFile;
    
    if (!fileToUse) {
      console.log("No current file");
      return;
    }

    // If we already have a cropped image, submit it
    if (this.hasCroppedImage) {
      console.log("Submitting cropped image");
      this.submitCroppedImage(fileToUse);
      return;
    }

    // Otherwise, open the perspective crop modal
    this.openPerspectiveCropModal();
  }

  openPerspectiveCropModal() {
    // Determine which file to use based on scan mode
    const fileToUse = this.scanMode === 'back_only' ? this.currentFileBack : this.currentFile;
    
    if (!fileToUse) {
      console.log("No current file for perspective crop");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      console.log("Dispatching show-perspective-crop event");
      const fileName = this.scanMode === 'back_only' ? this.originalFileNameBack : this.originalFileName;
      const suggestedCorners = this.scanMode === 'back_only' ? this.suggestedCornersBack : this.suggestedCorners;
      document.dispatchEvent(
        new CustomEvent("show-perspective-crop", {
          detail: {
            imageUrl: e.target.result,
            fileName: fileName,
            suggestedCorners: suggestedCorners,
          },
        }),
      );
    };
    reader.readAsDataURL(fileToUse);
  }



  handlePerspectiveCroppedImage(event) {
    console.log("Upload controller received perspective cropped image event:", event);
    const { file, originalFileName } = event.detail;
    console.log("Perspective cropped file details:", { file, originalFileName, size: file.size, type: file.type });

    // Store the crop in the same slot the modal was opened from
    // (see openPerspectiveCropModal), so submitCroppedImage picks it up
    if (this.scanMode === 'back_only') {
      this.currentFileBack = file;
      this.hasCroppedImageBack = true;
    } else {
      this.currentFile = file;
    }

    // Mark the cropped state before showPreview so it doesn't re-open the modal
    this.hasCroppedImage = true;
    this.showPreview(file, this.scanMode === 'back_only' ? 'back' : 'front');
    this.perspectiveCropButtonTarget.textContent = "Process Business Card";
    this.perspectiveCropButtonTarget.disabled = false;
    
    // Hide the scanning options after cropping
    this.hideUploadOptions();
  }

  submitCroppedImage(file) {
    console.log("Submitting cropped image:", file);
    
    // Handle both files if in both_sides mode
    if (this.scanMode === 'both_sides' && this.currentFile && this.currentFileBack) {
      // For both sides mode: back (with contact info) goes to business_card for AI processing
      // Front goes to business_card_back for display/storage
      const dataTransferMain = new DataTransfer();
      dataTransferMain.items.add(this.currentFileBack); // Back as main business_card for AI processing
      this.finalInputTarget.files = dataTransferMain.files;
      
      const dataTransferSecondary = new DataTransfer();
      dataTransferSecondary.items.add(this.currentFile); // Front as business_card_back for storage
      if (this.hasFinalInputBackTarget) {
        this.finalInputBackTarget.files = dataTransferSecondary.files;
      }
      
      console.log("Updated both file inputs (back for AI processing, front for display)");
    } else if (this.currentFileBack) {
      // Back only mode or only back file exists
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(this.currentFileBack);
      
      // In back-only mode, we store it as the main business_card
      if (this.scanMode === 'back_only') {
        this.finalInputTarget.files = dataTransfer.files;
      } else if (this.hasFinalInputBackTarget) {
        this.finalInputBackTarget.files = dataTransfer.files;
      }
    } else if (this.currentFile) {
      // Only front file exists
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(this.currentFile);
      this.finalInputTarget.files = dataTransfer.files;
    }
    
    console.log("Submitting form...");
    this.showProcessing("Processing business card and extracting contact information...");
    this.perspectiveCropButtonTarget.disabled = true;
    this.perspectiveCropButtonTarget.textContent = "Uploading...";
    
    // Safety timeout to re-enable button in case something goes wrong
    const timeoutId = setTimeout(() => {
      console.log("Form submission timeout - re-enabling button");
      this.hideProcessing();
      this.perspectiveCropButtonTarget.disabled = false;
      this.perspectiveCropButtonTarget.textContent = "Process Business Card";
    }, 30000); // 30 second timeout
    
    // Clear timeout if page unloads (successful submission)
    window.addEventListener('beforeunload', () => {
      clearTimeout(timeoutId);
    }, { once: true });
    
    this.finalFormTarget.submit();
  }

  hidePreview() {
    this.previewTarget.style.display = "none";
    this.previewContentTarget.innerHTML = "";
    this.perspectiveCropButtonTarget.disabled = true;
    
    // Reset the cropped image state
    this.hasCroppedImage = false;
    this.perspectiveCropButtonTarget.textContent = "Adjust Crop";
    
    // Show the scanning options again when file is removed
    this.showUploadOptions();
  }

  formatFileSize(bytes) {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }

  hideUploadOptions() {
    if (this.uploadIntroSection) {
      this.uploadIntroSection.style.display = "none";
    }
    if (this.uploadOptionsSection) {
      this.uploadOptionsSection.style.display = "none";
    }
  }

  showUploadOptions() {
    if (this.uploadIntroSection) {
      this.uploadIntroSection.style.display = "block";
    }
    if (this.uploadOptionsSection) {
      this.uploadOptionsSection.style.display = "grid";
    }
  }
}
